import { ArchiveIcon } from "@phosphor-icons/react/dist/csr/Archive";
import { ArrowClockwiseIcon } from "@phosphor-icons/react/dist/csr/ArrowClockwise";
import { DotsThreeIcon } from "@phosphor-icons/react/dist/csr/DotsThree";
import { DownloadSimpleIcon } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import { GearSixIcon } from "@phosphor-icons/react/dist/csr/GearSix";
import { PencilSimpleIcon } from "@phosphor-icons/react/dist/csr/PencilSimple";
import { UploadSimpleIcon } from "@phosphor-icons/react/dist/csr/UploadSimple";
import { UsersIcon } from "@phosphor-icons/react/dist/csr/Users";
import { type ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { canManageReport } from "@/profitability/access";
import {
  MarkReviewedButton,
  type usePeriodReview,
} from "@/profitability/overview/period-review";
import {
  profitabilityNewRunPath,
  profitabilityTemplateSettingsPath,
} from "@/profitability/routes";
import type {
  ProfitabilityReport,
  ProfitabilityRun,
} from "@/profitability/types";
import { useReportDownload } from "@/profitability/use-report-download";
import { useRerunReport } from "@/profitability/use-rerun-report";
import { ArchiveTemplateDialog } from "./archive-template-dialog";
import { ManageAccessDialog } from "./manage-access/manage-access-dialog";
import { RebuildTemplateButton } from "./rebuild-template-button";
import { useAgentNav } from "./use-agent-nav";

/**
 * The template page's own actions, after Ask Volca and Share: Edit agent
 * (the conversation that sets the agent up and changes it), Publish while
 * it is being set up and the week's upload once it runs, and the menu with the
 * period's workbook, running the period again, the template's Settings and
 * the occasional template actions. A manager the template was shared with
 * reads it: the menu keeps the workbook and Settings for them, and running,
 * editing, archiving and sharing stay finance's (Profitability::Access).
 */
export const TemplateActions = ({
  businessId,
  report,
  run,
  review,
  reviewLabel,
  publish,
}: {
  businessId: string;
  report: ProfitabilityReport;
  /** The run of the period on screen; null before the first one. */
  run: ProfitabilityRun | null;
  review: ReturnType<typeof usePeriodReview>;
  reviewLabel?: string;
  /** Publish, while the agent is being set up. */
  publish?: ReactNode;
}) => {
  const navigate = useNavigate();
  const nav = useAgentNav(!report.published);
  const [archiving, setArchiving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const { download, downloading } = useReportDownload(businessId, run);
  const { rerun, rerunning } = useRerunReport(businessId, run);
  const manages = canManageReport(report);
  const building = report.build_status === "building";
  const canRerun =
    manages && !!run && !report.archived_at && !building && !run.draft_sample;
  const busy = downloading || rerunning;
  return (
    <>
      {/* Building the agent is the page's work until it is published; after,
          changing it is occasional and lives in the menu, apart from Ask
          Volca, which is about the period. */}
      {manages && !report.archived_at && !report.published && (
        <Button variant="secondary" onClick={() => nav.edit()}>
          <PencilSimpleIcon aria-hidden />
          Edit agent
        </Button>
      )}
      {publish}
      {manages && report.published && !report.archived_at && (
        <Button onClick={() => navigate(profitabilityNewRunPath(report.id))}>
          <UploadSimpleIcon data-icon="inline-start" />
          Upload files
        </Button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="secondary"
              size="icon"
              aria-label="Agent actions"
              loading={busy}
              loadingLabel="Agent actions, in progress"
            />
          }
        >
          <DotsThreeIcon aria-hidden />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-52">
          {manages && run && (
            <>
              <MarkReviewedButton
                review={review}
                label={reviewLabel}
                asMenuItem
              />
              <DropdownMenuSeparator />
            </>
          )}
          {manages && !report.archived_at && report.published && (
            <>
              <DropdownMenuItem onClick={() => nav.edit()}>
                <PencilSimpleIcon />
                Edit agent
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {run?.workbook_available && (
            <DropdownMenuItem disabled={busy} onClick={download}>
              <DownloadSimpleIcon />
              Download workbook
            </DropdownMenuItem>
          )}
          {canRerun && (
            <DropdownMenuItem disabled={busy} onClick={() => rerun()}>
              <ArrowClockwiseIcon />
              Reprocess with current settings
            </DropdownMenuItem>
          )}
          {canRerun && run?.replayable && (
            <DropdownMenuItem disabled={busy} onClick={() => rerun("replay")}>
              <ArrowClockwiseIcon />
              Repeat original calculation
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() =>
              navigate(profitabilityTemplateSettingsPath(report.id))
            }
          >
            <GearSixIcon />
            Settings
          </DropdownMenuItem>
          {manages && !building && (
            <>
              <DropdownMenuSeparator />
              <RebuildTemplateButton businessId={businessId} report={report} />
              <DropdownMenuItem onClick={() => setSharing(true)}>
                <UsersIcon />
                Manage access…
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setArchiving(true)}>
                <ArchiveIcon />
                Archive agent
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <span role="status" className="sr-only">
        {downloading
          ? "Download workbook in progress"
          : rerunning
            ? "Rerun this period in progress"
            : ""}
      </span>
      {manages && (
        <>
          <ManageAccessDialog
            businessId={businessId}
            report={report}
            open={sharing}
            onOpenChange={setSharing}
          />
          <ArchiveTemplateDialog
            businessId={businessId}
            report={report}
            open={archiving}
            onOpenChange={setArchiving}
          />
        </>
      )}
    </>
  );
};
